import type { Template, PreviewLayout, OutputFormat } from '../types';
import type { FontSource } from '../packages/pdf-export/src/index';
import { getFormat } from '../formats/index';
import type { FormatRenderOptions, FormatRenderResult } from '../formats/types';

export interface PdfRenderResult {
  format: 'pdf';
  pdfBytes: Uint8Array;
  blob: Blob;
  url: string;
  pageCount: number;
  labelsPerPage: number;
}

export interface ZplRenderResult {
  format: 'zpl';
  zplString: string;
  blob: Blob;
  url: string;
  labelCount: number;
}

export type RenderResult = PdfRenderResult | ZplRenderResult;

export interface RenderOptions {
  template: Template;
  layout: PreviewLayout;
  labelCount: number;
  rotation?: 0 | 90 | 180 | 270;
  topOffset?: number;
  leftOffset?: number;
  qrIndexOffset?: number;
  data: {
    common?: Record<string, unknown>;
    coa?: Record<string, unknown>;
    product?: Record<string, unknown>;
    job?: Record<string, unknown>;
    qrValues?: string[];
  };
  legacyImages?: Record<string, string>;
  legacyFonts?: Record<string, string>;
  newAssetImages?: Record<string, string>;
  newAssetFonts?: Record<string, string>;
  fontSources?: FontSource[];
  defaultFont?: string;
  variableExamples?: Record<string, string>;
  retailIdData?: { packageTag: string; ranges: number[][] };
  isPreview?: boolean;
  hasCoa?: boolean;
}

//Dispatches to the format registry based on template.outputFormat
export async function renderLabels(options: RenderOptions): Promise<RenderResult> {
  const format: OutputFormat = options.template.outputFormat ?? 'pdf';
  const formatDef = getFormat(format);

  const renderOptions: FormatRenderOptions = {
    template: options.template,
    layout: options.layout,
    labelCount: options.labelCount,
    rotation: options.rotation,
    topOffset: options.topOffset,
    leftOffset: options.leftOffset,
    qrIndexOffset: options.qrIndexOffset,
    data: options.data,
    legacyImages: options.legacyImages,
    legacyFonts: options.legacyFonts,
    newAssetImages: options.newAssetImages,
    newAssetFonts: options.newAssetFonts,
    fontSources: options.fontSources,
    defaultFont: options.defaultFont,
    variableExamples: options.variableExamples,
    retailIdData: options.retailIdData,
    isPreview: options.isPreview,
    hasCoa: options.hasCoa,
  };

  const result: FormatRenderResult = await formatDef.renderLabels(renderOptions);

  return toRenderResult(result);
}

function toRenderResult(result: FormatRenderResult): RenderResult {
  if (result.format === 'pdf') {
    return {
      format: 'pdf',
      pdfBytes: (result.extra?.pdfBytes as Uint8Array) ?? new Uint8Array(),
      blob: result.blob,
      url: result.url,
      pageCount: (result.extra?.pageCount as number) ?? 1,
      labelsPerPage: (result.extra?.labelsPerPage as number) ?? 1,
    };
  }

  if (result.format === 'zpl') {
    return {
      format: 'zpl',
      zplString: (result.extra?.zplString as string) ?? '',
      blob: result.blob,
      url: result.url,
      labelCount: result.labelCount,
    };
  }

  //Future formats not in the legacy union fall through as generic
  return {
    format: result.format as any,
    blob: result.blob,
    url: result.url,
    labelCount: result.labelCount,
  } as any;
}
